import type { Video } from '@/types';
import { create } from 'zustand';

/**
 * Source of truth for the persistent player that lives inside
 * `src/app/(platform)/videos/layout.tsx`. The watch-route page writes the
 * current video (and the recommended list it should play through) into this
 * store on mount; the layout-level player reads it and swaps Shaka's source
 * via `player.load(newSrc)` without unmounting the `<video>` element.
 *
 * No persistence — stale entries across page reloads are worse than no entry
 * (the SSR fetch is the source of truth on cold loads).
 */
/**
 * An imperative "seek the mounted player to `t` seconds" request. The `nonce`
 * makes each request a fresh object so the player effect re-fires even when two
 * consecutive jumps target the same second. Drives per-video AI-chat jumps[].
 */
export interface PlayerSeekSignal {
  t: number;
  nonce: number;
}

interface PersistentPlayerState {
  video: Video | null;
  videos: Video[];
  autoplayEnabled: boolean;
  // Wall-clock at the moment the store learned about the current video.
  // Used to compute swap latency for `video_swap_first_frame`.
  lastSwapInitiatedAt: number | null;
  seekRequest: PlayerSeekSignal | null;
  // Bumped when the SSR poster is tapped before the player exists (pre-hydration
  // or empty store). The player reads it once on mount and starts playback.
  playRequestNonce: number;
  setPlayData: (video: Video, videos: Video[]) => void;
  setOptimisticVideo: (video: Video) => void;
  setAutoplayEnabled: (enabled: boolean) => void;
  requestSeek: (t: number) => void;
  requestPlay: () => void;
  clear: () => void;
}

export const usePersistentPlayerStore = create<PersistentPlayerState>((setState) => ({
  video: null,
  videos: [],
  autoplayEnabled: false,
  lastSwapInitiatedAt: null,
  seekRequest: null,
  playRequestNonce: 0,
  setPlayData: (video, videos) => setState({ video, videos, lastSwapInitiatedAt: Date.now() }),
  setOptimisticVideo: (video) => setState({ video, lastSwapInitiatedAt: Date.now() }),
  setAutoplayEnabled: (enabled) => setState({ autoplayEnabled: enabled }),
  requestSeek: (t) =>
    setState((s) => ({ seekRequest: { t, nonce: (s.seekRequest?.nonce ?? 0) + 1 } })),
  requestPlay: () => setState((s) => ({ playRequestNonce: s.playRequestNonce + 1 })),
  clear: () =>
    setState({
      video: null,
      videos: [],
      lastSwapInitiatedAt: null,
      seekRequest: null,
      playRequestNonce: 0,
    }),
}));

/** Poster tap before the player mounted — the player honours it on mount. */
export function requestPlayerPlay(): void {
  usePersistentPlayerStore.getState().requestPlay();
}

/** Imperative seek — call from anywhere (e.g. the per-video Ask panel jumps). */
export function requestPlayerSeek(t: number): void {
  usePersistentPlayerStore.getState().requestSeek(t);
}

// Playhead stash — deliberately NOT zustand state: it updates on every
// timeupdate and nothing should re-render with it. Read once on demand
// (e.g. seeding the share-clip create panel at the current moment).
let playheadSeconds = 0;

export function setPlayheadSeconds(t: number): void {
  playheadSeconds = t;
}

export function getPlayheadSeconds(): number {
  return playheadSeconds;
}

/** Non-reactive read for code that runs outside React render (effects, refs). */
export function readPersistentPlayer(): { video: Video | null; videos: Video[] } {
  const { video, videos } = usePersistentPlayerStore.getState();
  return { video, videos };
}

/** Imperative write — call from the watch-route page on mount and on uuid change. */
export function setPersistentPlayerData(video: Video, videos: Video[]): void {
  usePersistentPlayerStore.getState().setPlayData(video, videos);
}

/**
 * Optimistic write at click time — paints the player frame for the next video
 * before the page's server render completes. Preserves the existing `videos[]`
 * recommended list so next/prev nav stays valid through the swap; the page's
 * `<WatchPlayDataHydrator>` overwrites with the full payload (description,
 * comments, user_progress) once the SSR fetch resolves.
 */
export function setOptimisticPlayerVideo(video: Video): void {
  usePersistentPlayerStore.getState().setOptimisticVideo(video);
}

/** Clear when navigating away from the watch route. */
export function clearPersistentPlayer(): void {
  usePersistentPlayerStore.getState().clear();
}
